import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import global from "./../../scss/dhp.scss";

let style = Object.assign({}, global);

const DescriptionAutoResizingTextarea = ({
  inputFieldValue,
  handleBlur,
  handleChange,
  setTooltipTarget,
  setTooltipFor,
  slug,
  sectionIdx,
  itemIdx,
  setShowTooltip,
  showTooltip,
  setDefaltFontBold,
  setBulletActive,
  setBulletDisable,
  maxCharacter,
  placeHolderShow,
  defaultHeight,
  minHeight,
  maxHeight,
  addSectionItem,
  autoGrow = false,
  ...props
}) => {
  const lastSelectedTextRef = useRef("");
  const editorRef = useRef(null);
  const currentValueRef = useRef("");
  const buildTooltipId = () => {
  const base = 'tooltip-anchor';
  const parts = [
    sectionIdx != null ? `sec-${sectionIdx}` : null,
    itemIdx != null ? `item-${itemIdx}` : null,
    slug ? `slug-${slug}` : null,
  ].filter(Boolean);

  const randomSuffix = Math.random().toString(36).substr(2, 9);

  return [base, ...parts, randomSuffix].join('-');
  };

  const tooltipAnchorId = useRef(buildTooltipId());

  const [isFocused, setIsFocused] = useState(false);
  const [descriptionFieldIsEmpty, setDescriptionFieldIsEmpty] = useState(true);
  const [showDescriptionBorder, setShowDescriptionBorder] = useState(false);
  const [showEllipses, setShowEllipses] = useState(autoGrow ? false : true);

  const placeCursorAtEnd = () => {
    const editor = editorRef.current;
    if (!editor) return;
    const range = document.createRange();
    range.selectNodeContents(editor);
    range.collapse(false); // to end
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  };

  const getVisibleTextLength = element => {
    if (!element) {
      return 0;
    }
    let text = element.innerText || "";
    return text.length;
  };

  //Initialize with existing HTML
  useEffect(() => {
    if (editorRef.current) {
      const newHtml = inputFieldValue || "";
      const isEffectivelyEmpty =
        !newHtml || newHtml === "<div><br></div>" || newHtml === "<br>" || newHtml.trim() === "";
      setDescriptionFieldIsEmpty(isEffectivelyEmpty);

      if (inputFieldValue !== editorRef.current.innerHTML) {
        editorRef.current.innerHTML = newHtml;
        currentValueRef.current = newHtml;
      }
    }
  }, [inputFieldValue]);

  //Adjust height dynamically
  const adjustHeight = () => {
    if(autoGrow) return;
    const editor = editorRef.current;
    if (!editor) return;

    if (editorRef.current) {
      //Temporarily set to default height to measure scrollHeight accurately
      editorRef.current.style.height = `${defaultHeight}px`;

      const scrollHeight = editorRef.current.scrollHeight;
      let newHeight = Math.min(scrollHeight, maxHeight);
      if (newHeight < maxHeight) newHeight = newHeight + 5;
      //Apply the new height
      editorRef.current.style.height = `${newHeight < defaultHeight ? defaultHeight : newHeight}px`;
    }
  };

  //Handle focus/blur
  const handleFocus = () => {
    setIsFocused(true);
    setShowTooltip(false);
    setTooltipTarget(null);
    setShowDescriptionBorder(true);
    setShowEllipses(false);

    if (!showTooltip) {
      const editor = editorRef.current;
      if (editor) {
        if(!autoGrow) editor.style.height = `${defaultHeight}px`;
        adjustHeight();
      }
    }
  };

  const handleLocalBlur = (e) => {
    const editor = editorRef.current;
    const html = editorRef.current?.innerText.trim();

    if (!showTooltip && editor && !autoGrow) {
      editor.style.height = `${minHeight}px`;
      editor.scrollTop = 0; //reset scroll instantly
      setShowEllipses(true);
    }
    setShowDescriptionBorder(false);
    setIsFocused(false);
    setShowTooltip(false);
    setTooltipTarget(null);
    handleChange({ target: { value: editor.innerHTML } });
    if (handleBlur) handleBlur(slug, sectionIdx, itemIdx);

    if(addSectionItem){
      const rawNext = e.relatedTarget;
      const nextEl = rawNext instanceof HTMLElement ? rawNext : null;
      const addTaskEl = nextEl?.closest?.('[data-role="add-task"]');

      if (addTaskEl && typeof addSectionItem === "function") addSectionItem("item", sectionIdx);
    }
  };

  const handlePaste = e => {
    const editor = editorRef.current;
    if (!editor) return;

    e.preventDefault();

    const clipboardData = e.clipboardData || window.clipboardData;
    const pastedText = clipboardData.getData("text/plain");
    const contentToPaste = pastedText;

    const currentLength = editor.innerText.length;
    const pasteLength = contentToPaste.length;

    //Enforce character limit
    if (currentLength >= maxCharacter) return;

    //If it would exceed the limit, trim the pasted content
    const availableSpace = maxCharacter - currentLength;
    const textToInsert = contentToPaste.slice(0, availableSpace);

    //Use Range API (modern and reliable)
    const selection = window.getSelection();
    if (!selection.rangeCount) return;

    const range = selection.getRangeAt(0);
    range.deleteContents();

    const textNode = document.createTextNode(textToInsert);
    range.insertNode(textNode);

    //Move cursor to the end of inserted text
    range.setStartAfter(textNode);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);

    //Trigger height + change update
    adjustHeight();
    handleLocalChange({ currentTarget: editor });
  };

  //On input change
  const handleLocalChange = e => {
    adjustHeight();

    const editor = e?.currentTarget || editorRef.current;
    if (!editor) return;

    const currentLength = getVisibleTextLength(editor);

    if (currentLength > maxCharacter) {
      //Restore the previous content and place cursor at end
      editor.innerHTML = currentValueRef.current;
      placeCursorAtEnd();
      return;
    }

    //if completely empty or has only text directly under root
    if (editor.innerText.trim().length === 0 || [...editor.childNodes].some(node => node.nodeType === Node.TEXT_NODE)) {
      const wrapper = document.createElement("div");
      wrapper.innerHTML = "<br>";

      //Move text nodes into wrapper if any exist
      while (editor.firstChild) {
        if (editor.firstChild.nodeType === Node.TEXT_NODE) {
          wrapper.appendChild(editor.firstChild);
        } else {
          editor.removeChild(editor.firstChild);
        }
      }

      editor.appendChild(wrapper);

      //Move caret inside the wrapper
      const range = document.createRange();
      range.selectNodeContents(wrapper);
      range.collapse(true);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }

    const text = editor.innerText.trim();
    setDescriptionFieldIsEmpty(text.length === 0);

    const newHtml = editor.innerHTML;
    currentValueRef.current = newHtml;

    if (handleChange) {
      handleChange({ target: { value: editor.innerHTML } });
    }
  };

  const isSelectionBold = () => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return false;
    const node = sel.anchorNode;

    // If text node, get its parent element
    const el = node.nodeType === 3 ? node.parentElement : node;

    // Check up the DOM tree for bold style
    return !!el.closest("span[style*='font-weight:700']");
  }

  const isSelectionInBulletList = () => {
    const sel = window.getSelection();
    if (!sel.rangeCount) return false;
    const node = sel.anchorNode;
    const el = node.nodeType === 3 ? node.parentElement : node;

    return !!el.closest("ul");
  }

  const normalizeLiBeforeSplit = (li) => {
    if (!li || li.tagName !== "LI") return;

    let inner = li.querySelector("div");

    // create inner div if missing
    if (!inner) {
      inner = document.createElement("div");
      while (li.firstChild) inner.appendChild(li.firstChild);
      li.appendChild(inner);
      return;
    }

    // move stray nodes inside inner div
    const children = Array.from(li.childNodes);
    for (const node of children) {
      if (node !== inner) {
        inner.appendChild(node); // move re you INSIDE the div
      }
    }

    inner.normalize();
  }

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (!selection.rangeCount) return;
    const selectedText = selection.toString().trim();

    const range = selection.getRangeAt(0);

    const normalizeToElement = node => (node.nodeType === Node.TEXT_NODE ? node.parentNode : node);

    let startNode = normalizeToElement(range.startContainer);
    let endNode = normalizeToElement(range.endContainer);

    const startDiv = startNode.closest("div");

    const editor = startDiv.closest('[contenteditable="true"]');
    if (!editor) return;

    if (!selectedText) {
      setShowTooltip(false);
      lastSelectedTextRef.current = "";
      return;
    }

    const allULs = Array.from(editor.querySelectorAll("ul.unorder-list, ul.order-list"));
    const allDivs = Array.from(editor.querySelectorAll(":scope > div"));

    let selectionHasUL = false;
    let selectionHasDiv = false;

    [...allULs, ...allDivs].forEach(el => {
      const elRange = document.createRange();
      elRange.selectNodeContents(el);
      if (
        range.compareBoundaryPoints(Range.END_TO_START, elRange) < 0 &&
        range.compareBoundaryPoints(Range.START_TO_END, elRange) > 0
      ) {
        if (el.tagName === "UL") selectionHasUL = true;
        if (el.tagName === "DIV") selectionHasDiv = true;
      }
    });

    if (selectionHasUL && selectionHasDiv) {
      setBulletDisable(true);
      return;
    } else {
      setBulletDisable(false);
    }

    const alreadyBold = isSelectionBold(selection);
    setDefaltFontBold(alreadyBold);

    const alreadyBullet = isSelectionInBulletList();
    setBulletActive(alreadyBullet);
  };

  const splitListItemAtCaret = (li) => {
    if (!li || li.tagName !== "LI") return null;

    normalizeLiBeforeSplit(li);

    // ensure canonical inner div
    let inner = li.querySelector("div");
    if (!inner) {
      inner = document.createElement("div");
      while (li.firstChild) inner.appendChild(li.firstChild);
      li.appendChild(inner);
    }

    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const range = sel.getRangeAt(0);

    if ((inner.textContent || "").trim() === "") {
      const ul = li.closest("ul, ol");
      const parentDiv = ul ? ul.closest("div") : null;
      li.remove();
      if (ul && !ul.querySelector("li")) {
        // replace empty list with a blank div (same as your original)
        if (ul.parentNode) {
          const newDiv = document.createElement("div");
          newDiv.innerHTML = "<br>";
          ul.parentNode.insertBefore(newDiv, ul.nextSibling);
          ul.remove();
          const r = document.createRange();
          r.selectNodeContents(newDiv);
          r.collapse(true);
          sel.removeAllRanges();
          sel.addRange(r);
        }
      }
      return null;
    }

    // Build range from caret to end of inner
    const after = document.createRange();
    try {
      after.setStart(range.endContainer, range.endOffset);

      // set end at very end of inner (last descendant)
      let last = inner;
      while (last.lastChild) last = last.lastChild;
      if (last.nodeType === Node.TEXT_NODE) {
        after.setEnd(last, last.nodeValue ? last.nodeValue.length : 0);
      } else {
        after.setEndAfter(last);
      }
    } catch {
      after.selectNodeContents(inner);
      after.collapse(false);
    }

    // extract trailing content and create new li
    const trailing = after.extractContents();
    const newLi = document.createElement("li");
    const newDiv = document.createElement("div");
    if (!trailing || !(trailing.textContent || "").trim()) {
      newDiv.innerHTML = "<br>";
    } else {
      newDiv.appendChild(trailing);
    }
    newLi.appendChild(newDiv);

    // insert new li after current
    const parent = li.parentNode;
    parent.insertBefore(newLi, li.nextSibling);

    // normalize/ensure placeholders
    inner.normalize();
    newDiv.normalize();
    if (!(inner.textContent || "").trim()) inner.innerHTML = "<br>";

    // place caret at start of newDiv
    const newRange = document.createRange();
    const firstText = (function findFirstTextNode(n) {
      if (!n) return null;
      if (n.nodeType === 3) return n;
      for (let c = n.firstChild; c; c = c.nextSibling) {
        const t = findFirstTextNode(c);
        if (t) return t;
      }
      return null;
    })(newDiv);

    if (firstText) newRange.setStart(firstText, 0);
    else {
      newRange.selectNodeContents(newDiv);
      newRange.collapse(true);
    }

    sel.removeAllRanges();
    sel.addRange(newRange);

    return newLi;
  }

  const splitDivAtCaret = (currentDiv) => {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;

    const range = sel.getRangeAt(0);

    // Ensure the currentDiv contains a text node to split
    let textNode = range.startContainer;
    if (textNode === currentDiv) {
      textNode = currentDiv.firstChild || currentDiv.appendChild(document.createTextNode(""));
    }

    // Build range from caret to end of currentDiv
    const after = document.createRange();
    try {
      after.setStart(range.startContainer, range.startOffset);

      // Set end at last node in currentDiv
      let last = currentDiv;
      while (last.lastChild) last = last.lastChild;

      if (last.nodeType === Node.TEXT_NODE) {
        after.setEnd(last, last.nodeValue.length);
      } else {
        after.setEndAfter(last);
      }
    } catch {
      after.selectNodeContents(currentDiv);
      after.collapse(false);
    }

    // Extract the trailing content
    const trailing = after.extractContents();

    // Create the new div
    const newDiv = document.createElement("div");
    if ((trailing.textContent || "").trim()) {
      newDiv.appendChild(trailing);
    } else {
      newDiv.innerHTML = "<br>";
    }

    // Insert new div after currentDiv
    currentDiv.insertAdjacentElement("afterend", newDiv);

    // Move caret to start of new div
    const newRange = document.createRange();

    let firstText = newDiv;
    while (firstText && firstText.firstChild) {
      firstText = firstText.firstChild;
    }

    if (firstText && firstText.nodeType === Node.TEXT_NODE) {
      newRange.setStart(firstText, 0);
    } else {
      newRange.selectNodeContents(newDiv);
      newRange.collapse(true);
    }

    sel.removeAllRanges();
    sel.addRange(newRange);

    return newDiv;
  }

  useEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;

    if (editor.innerHTML.trim() === "") {
      editor.innerHTML = "<div><br></div>";
    }

    const handleKeyDown = e => {
      const selection = window.getSelection();
      if (!selection.rangeCount) return;

      const range = selection.getRangeAt(0);
      let currentNode = range.startContainer;

      if (currentNode.nodeType === Node.TEXT_NODE) {
        currentNode = currentNode.parentNode;
      }

      const currentDiv = currentNode.closest("div");
      const li = currentNode.closest("li");
      const ul = li?.closest("ul");

      if (e.key === "Enter" && e.shiftKey) {
        e.preventDefault();

        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        const range = selection.getRangeAt(0);

        const li =
          range.startContainer.nodeType === 3
            ? range.startContainer.parentElement.closest("li")
            : range.startContainer.closest("li");

        const br = document.createElement("br");
        const zwsp = document.createTextNode("\u200B");

        if (li && li.contains(range.startContainer)) {
          range.insertNode(br);
          range.collapse(false);
          range.insertNode(zwsp);
        } else {
          range.insertNode(br);
          range.collapse(false);
          range.insertNode(zwsp);
        }

        range.setStartAfter(zwsp);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        return;
      }

      if (e.key === "Enter" && !e.shiftKey && li && ul) {
        e.preventDefault();
        const created = splitListItemAtCaret(li);
        if (created) {
          // optional: notify app of change
          if (typeof handleLocalChange === "function") handleLocalChange();
        }
        return;
      }

      if (e.key === "Enter" && !e.shiftKey && !li) {
        e.preventDefault();

        if (currentDiv) {
          splitDivAtCaret(currentDiv);
        } else {
          // fallback: just add a blank div
          const newDiv = document.createElement("div");
          newDiv.innerHTML = "<br>";
          editorRef.current.appendChild(newDiv);

          const r = document.createRange();
          r.selectNodeContents(newDiv);
          r.collapse(true);

          const sel = window.getSelection();
          sel.removeAllRanges();
          sel.addRange(r);
        }

        handleLocalChange();
        return;
      }

      if (e.key === "Backspace" && li && ul) {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;
        const range = selection.getRangeAt(0);

        if (!range.collapsed) {
          return;
        }

        const liDiv = li.querySelector("div") || li;

        const visibleText = node => (node.textContent || "").replace(/\u200B/g, "").trim();

        let splitAfterBr = false;
        try {
          if (range.collapsed) {
            const sc = range.startContainer;
            const so = range.startOffset;

            if (
              sc.nodeType === Node.TEXT_NODE &&
              so === 0 &&
              sc.previousSibling &&
              sc.previousSibling.nodeName === "BR"
            ) {
              splitAfterBr = true;
            }

            if (!splitAfterBr && sc.nodeType !== Node.TEXT_NODE) {
              const child = sc.childNodes[so] || null;
              const prev = child ? child.previousSibling : null;
              if (prev && prev.nodeName === "BR") splitAfterBr = true;
            }
          }
        } catch (err) {
          splitAfterBr = false;
        }

        if (splitAfterBr) {
          e.preventDefault();

          const liDiv = li.querySelector("div") || li;
          const selection = window.getSelection();

          const nextLis = [];
          let next = li.nextElementSibling;
          while (next) {
            nextLis.push(next);
            next = next.nextElementSibling;
          }

          let brNode = null;
          const sc = range.startContainer;
          if (sc.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
            brNode = sc.previousSibling && sc.previousSibling.nodeName === "BR" ? sc.previousSibling : null;
          } else {
            const child = sc.childNodes[range.startOffset] || null;
            const prev = child ? child.previousSibling : null;
            if (prev && prev.nodeName === "BR") brNode = prev;
          }

          if (!brNode) return;

          const newDiv = document.createElement("div");

          let afterNode = brNode.nextSibling;
          while (afterNode) {
            const toMove = afterNode;
            afterNode = afterNode.nextSibling;
            newDiv.appendChild(toMove);
          }

          brNode.remove();

          if (nextLis.length > 0) {
            const secondUL = ul.cloneNode(false);

            nextLis.forEach(nextLi => {
              secondUL.appendChild(nextLi);
            });

            ul.parentNode.insertBefore(newDiv, ul.nextSibling);
            ul.parentNode.insertBefore(secondUL, newDiv.nextSibling);
          } else {
            ul.parentNode.insertBefore(newDiv, ul.nextSibling);
          }

          const visibleText = liDiv.textContent.replace(/\u200B/g, "").trim();
          if (visibleText.length === 0) {
            liDiv.innerHTML = "<br>";
          }

          const newRange = document.createRange();
          newRange.selectNodeContents(newDiv);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);

          return;
        }

        const isEmpty = visibleText(liDiv).length === 0;
        if (isEmpty) {
          e.preventDefault();

          const selection = window.getSelection();
          if (!selection) return;

          const prevLi = li.previousElementSibling;
          const nextLi = li.nextElementSibling;

          const isOnlyItem = !prevLi && !nextLi;
          const isLastItem = !!prevLi && !nextLi;
          const editor = editorRef.current;

          if (isOnlyItem) {
            const newDiv = document.createElement("div");
            newDiv.innerHTML = "<br>";

            if (ul.parentNode) {
              ul.replaceWith(newDiv);
            } else if (editor) {
              editor.appendChild(newDiv);
              ul.remove();
            }

            const r = document.createRange();
            r.selectNodeContents(newDiv);
            r.collapse(true);
            selection.removeAllRanges();
            selection.addRange(r);
            return;
          }

          if (isLastItem) {
            li.remove();

            const newDiv = document.createElement("div");
            newDiv.innerHTML = "<br>";

            if (ul.parentNode) {
              ul.parentNode.insertBefore(newDiv, ul.nextSibling);
              if (!ul.querySelector("li")) {
                ul.remove();
              }
            } else if (editor) {
              editor.appendChild(newDiv);
            }

            const r = document.createRange();
            r.selectNodeContents(newDiv);
            r.collapse(true);
            selection.removeAllRanges();
            selection.addRange(r);
            return;
          }

          li.remove();

          if (!ul.querySelector("li")) {
            const newDiv = document.createElement("div");
            newDiv.innerHTML = "<br>";
            ul.replaceWith(newDiv);

            const r = document.createRange();
            r.selectNodeContents(newDiv);
            r.collapse(true);
            selection.removeAllRanges();
            selection.addRange(r);
          } else if (prevLi) {
            const prevDiv = prevLi.querySelector("div") || prevLi;
            const r = document.createRange();
            r.selectNodeContents(prevDiv);
            r.collapse(false);
            selection.removeAllRanges();
            selection.addRange(r);
          }
          return;
        }

        let caretAtStart = false;
        try {
          if (!li.contains(range.startContainer)) {
            caretAtStart = false;
          } else {
            const preRange = document.createRange();
            preRange.setStart(liDiv, 0);
            preRange.setEnd(range.startContainer, range.startOffset);
            const preText = preRange.cloneContents().textContent || "";
            caretAtStart = preText.replace(/\u200B/g, "").trim().length === 0;
          }
        } catch (err) {
          caretAtStart = false;
        }

        if (caretAtStart) {
          const prevLi = li.previousElementSibling;
          if (prevLi) {
            e.preventDefault();

            const prevDiv = prevLi.querySelector("div") || prevLi;
            const src = liDiv;

            const br = document.createElement("br");
            prevDiv.appendChild(br);

            const frag = document.createDocumentFragment();
            while (src.firstChild) frag.appendChild(src.firstChild);
            prevDiv.appendChild(frag);

            li.remove();

            const afterBr = br.nextSibling;
            const newRange = document.createRange();
            if (!afterBr) {
              newRange.selectNodeContents(prevDiv);
              newRange.collapse(false);
            } else if (afterBr.nodeType === Node.TEXT_NODE) {
              newRange.setStart(afterBr, 0);
              newRange.collapse(true);
            } else {
              newRange.setStart(afterBr, 0);
              newRange.collapse(true);
            }
            selection.removeAllRanges();
            selection.addRange(newRange);
            return;
          } else {
            e.preventDefault();

            const newDiv = document.createElement("div");
            newDiv.innerHTML = liDiv ? liDiv.innerHTML : li.innerHTML;

            const nextLis = [];
            let next = li.nextElementSibling;
            while (next) {
              nextLis.push(next);
              next = next.nextElementSibling;
            }

            if (nextLis.length > 0) {
              const secondUL = ul.cloneNode(false);
              nextLis.forEach(n => secondUL.appendChild(n));

              li.remove();

              if (ul && ul.parentNode) {
                ul.parentNode.insertBefore(newDiv, ul);
              } else {
                const editor = editorRef?.current;
                if (editor) editor.appendChild(newDiv);
              }

              if (!ul.querySelector("li")) ul.remove();

              if (ul && ul.parentNode) {
                ul.parentNode.insertBefore(secondUL, newDiv.nextSibling);
              } else {
                const editor = editorRef?.current;
                if (editor) editor.appendChild(secondUL);
              }
            } else {
              li.remove();
              ul.parentNode.insertBefore(newDiv, ul.nextSibling);
              if (!ul.querySelector("li")) ul.remove();
            }

            const newRange = document.createRange();
            newRange.selectNodeContents(newDiv);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);

            return;
          }
        }
      }
    };
    editor.addEventListener("keydown", handleKeyDown);
    return () => editor.removeEventListener("keydown", handleKeyDown);
  }, []);

  useEffect(() => {
    let lastRange = null;

    const handleSelectionChange = () => {
      const selection = window.getSelection();
      if (!selection.rangeCount) {
        setShowTooltip(false);
        lastRange = null;
        return;
      }

      const range = selection.getRangeAt(0);
      const editor = editorRef.current;

      if (!editor || !editor.contains(range.commonAncestorContainer)) {
        if (showTooltip) {
          setShowTooltip(false);
        }
        return;
      }

      const text = selection.toString().trim();

      if (!text) {
        if (lastRange) {
          setShowTooltip(false);
          lastRange = null;
        }
        return;
      }
      //Check if the range actually changed
      if (
        !lastRange ||
        range.startContainer !== lastRange.startContainer ||
        range.startOffset !== lastRange.startOffset ||
        range.endContainer !== lastRange.endContainer ||
        range.endOffset !== lastRange.endOffset
      ) {
        lastRange = range.cloneRange();

        //Move tooltip position
        const rect = range.getBoundingClientRect();

        let fakeAnchor = document.getElementById(tooltipAnchorId.current);
        if (!fakeAnchor) {
          fakeAnchor = document.createElement("div");
          fakeAnchor.id = tooltipAnchorId.current;
          fakeAnchor.style.position = "absolute";
          fakeAnchor.style.pointerEvents = "none";
          document.body.appendChild(fakeAnchor);
        }

        fakeAnchor.style.top = `${rect.top + window.scrollY}px`;
        fakeAnchor.style.left = `${rect.left + window.scrollX}px`;

        setTooltipTarget(fakeAnchor);
        setTooltipFor({
          formattingOpt: true,
          enableBullet: true,
        });
        setShowTooltip(true);
      }
    };

    document.addEventListener("selectionchange", handleSelectionChange);
    return () => document.removeEventListener("selectionchange", handleSelectionChange);
  }, []);

  useEffect(() => {
    return () => {
      // Clean up tooltip anchor when component unmounts
      const anchor = document.getElementById(tooltipAnchorId.current);
      if (anchor) {
        anchor.remove();
      }
    };
  }, []);

  return (
    <div className="custom-description-auto-resize">
      <div
        id="description-editor"
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning={true}
        onInput={handleLocalChange}
        onFocus={handleFocus}
        onBlur={handleLocalBlur}
        onKeyUp={handleTextSelection}
        onMouseUp={handleTextSelection}
        // onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        className={cx(style["form-control"], { [style["active"]]: showDescriptionBorder })}
        style={{
          ...(autoGrow && { minHeight: `${defaultHeight}px` }),
          height: autoGrow ? "auto" : `${minHeight}px`,
          maxHeight: autoGrow ? "none" : `${maxHeight}px`,
          overflowY: autoGrow ? "hidden" : (isFocused ? "auto" : "hidden"),
          padding: "0.375rem 1rem",
          whiteSpace: "pre-wrap",
          wordBreak: "normal",
          lineHeight: "normal",
          ...(showEllipses && {
            display: "-webkit-box",
            WebkitBoxOrient: "vertical",
            WebkitLineClamp: 2,
            overflow: "hidden",
          }),
        }}
        {...props}
      />
      {placeHolderShow && (
        <div
          className={cx(style["custom-description-auto-resize-placeholder"], {
            [style["hide-placeholder"]]: !descriptionFieldIsEmpty,
          })}>
          {`Description (Max ${maxCharacter} characters)`}
        </div>
      )}
    </div>
  );
};

export default DescriptionAutoResizingTextarea;
